import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, model, signal, untracked } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ListViewInsuredComponent } from './components/listview-insured/list-view-insured.component'
import { Panel } from 'primeng/panel'
import { PermissionService } from '@core/auth/services/permission.service'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { filter } from 'rxjs/operators'
import { CommonModule } from '@angular/common'
import { ListViewContentUnitComponent } from '@app/layout/sidebar/components/listview-cu/list-view-cu.component'
import { SIDEBAR_CONTENT_TYPE, SIDEBAR_ROUTES } from '@app/layout/sidebar/models/sidebar.constants'
import { ListViewTemplateComponent } from './components/listview-template/list-view-template.component'
import { TemplateSidebarBridgeService } from '@features/template/services/template-sidebar-bridge.service'
import { ListViewFilterComponent } from '@app/layout/sidebar/components/listview-filter/list-view-filter.component'
import { CategoryService } from '@app/core/auth/services/category.service'
import { ListViewChComponent } from '@app/layout/sidebar/components/listview-ch/list-view-ch.component'
import {
  ListViewInsuredCertificatesComponent
} from '@app/layout/sidebar/components/listview-insured/list-view-insured-certificates/list-view-insured-certificates.component'
import { SelectedInsuredService } from '@app/layout/sidebar/services/selected-insured.service'

@Component({
  selector: 'albert-sidebar',
  standalone: true,
  imports: [ListViewInsuredComponent, Panel, CommonModule, ListViewContentUnitComponent, ListViewTemplateComponent, ListViewFilterComponent,
    ListViewChComponent, ListViewInsuredCertificatesComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent {
  public readonly sidebarDrawerVisible = model(false)
  public readonly permissionService = inject(PermissionService)
  public readonly categoryService = inject(CategoryService)
  public readonly listViewContent = model<string>(SIDEBAR_CONTENT_TYPE.INSURED)
  public readonly insuredListExpanded = signal(false)
  public readonly showInsuredCertificates = computed(() => {
    if (this.insuredListExpanded()) {
      return false
    }
    if (this.selectedInsuredUuid() === null) {
      return false
    }
    if (this.listViewContent() === SIDEBAR_CONTENT_TYPE.CERTIFICATES) {
      return true
    }
    return this.listViewContent() === SIDEBAR_CONTENT_TYPE.INSURED
      && this.permissionService.canView(this.categoryService.activeCategory())
  })
  public readonly displayedContent = computed(() => {
    const content = this.listViewContent()
    const showsInsuredList = this.insuredListExpanded()
      || (content === SIDEBAR_CONTENT_TYPE.CERTIFICATES && !this.showInsuredCertificates())
    return showsInsuredList ? SIDEBAR_CONTENT_TYPE.INSURED : content
  })
  private readonly selectedInsured = inject(SelectedInsuredService)
  public readonly selectedInsuredUuid = this.selectedInsured.selectedUuid
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly destroyRef = inject(DestroyRef)
  private readonly templateBridge = inject(TemplateSidebarBridgeService)

  constructor() {
    effect(() => {
      const isTemplateEditMode = this.templateBridge.isTemplateEditMode()
      untracked(() => {
        if (isTemplateEditMode) {
          this.listViewContent.set(SIDEBAR_CONTENT_TYPE.CONTENT_UNITS)
        } else {
          this.updateContent(this.router.url)
        }
      })
    })

    this.updateContent(this.router.url)
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: NavigationEnd) => {
      this.updateContent(event.urlAfterRedirects)
    })
  }

  public expandInsuredList(): void {
    this.insuredListExpanded.set(true)
  }

  public collapseInsuredList(): void {
    this.insuredListExpanded.set(false)
  }

  private updateContent(url: string): void {
    if (this.templateBridge.isTemplateEditMode()) {
      this.listViewContent.set(SIDEBAR_CONTENT_TYPE.CONTENT_UNITS)
      return
    }

    let type: string = SIDEBAR_CONTENT_TYPE.INSURED

    if (url.includes(SIDEBAR_ROUTES.CONTENT_UNIT_PATH)) {
      type = SIDEBAR_CONTENT_TYPE.CONTENT_UNITS
    } else if (url.includes(SIDEBAR_ROUTES.TEMPLATE_PATH)) {
      type = SIDEBAR_CONTENT_TYPE.TEMPLATES
    } else if (url.includes(SIDEBAR_ROUTES.CERTIFICATE_HOLDER_PATH)) {
      type = SIDEBAR_CONTENT_TYPE.CERTIFICATE_HOLDERS
    } else if (url.includes(SIDEBAR_ROUTES.CERTIFICATE_PATH)) {
      type = SIDEBAR_CONTENT_TYPE.CERTIFICATES
    }

    this.listViewContent.set(type)
    this.updateSelectedInsured(type)
  }

  private updateSelectedInsured(contentType: string): void {
    const routeInsuredUuid = this.getInsuredUuidFromRoute() ?? this.getInsuredUuidFromQueryParams()
    if (routeInsuredUuid !== null) {
      this.selectedInsured.select(routeInsuredUuid)
      this.collapseInsuredList()
      return
    }

    // Keep the insured selected while one of its certificates is open, so the rail keeps
    // its highlight and the certificate list stays scoped to that insured.
    if (contentType !== SIDEBAR_CONTENT_TYPE.CERTIFICATES) {
      this.selectedInsured.clear()
    }
  }

  private getInsuredUuidFromQueryParams(): string | null {
    const insuredUuid = this.route.snapshot.queryParamMap.get('insuredUuid')
    return insuredUuid !== null && insuredUuid !== '' ? insuredUuid : null
  }

  private getInsuredUuidFromRoute(): string | null {
    let route: ActivatedRoute | null = this.route.root
    while (route) {
      if (route.snapshot.routeConfig?.path === 'insured/:uuid') {
        return route.snapshot.paramMap.get('uuid')
      }
      route = route.firstChild
    }
    return null
  }
}
